/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { CirclePlus, ShieldCheck, ShieldOff } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Input } from '@/components/ui/input';
import { SystemReason } from '@/components/SystemReason';
import { api, type DomainDetail } from '../../api';
import type { AvailableAddon, DomainAddon } from '../../api/areas/services';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, DataTable, Field, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EmptyState, Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatDate } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { FactRows, Stepper, Timeline, TwoColumn } from '../../components/flow';
import { Amount } from '../commerce/shared';
import { ActionDialog, FormFooter, ReasonAction, ReasonField, selectClass, type DialogBodyProps } from '../operator/shared';
import { PendingChangesTable } from './ProtectedPage';
import { useEnum, useLocalName } from './shared';

const STATE_TONE: Record<string, Tone> = { ORDERED: 'pending', ACTIVE: 'ok', CANCELLED: 'neutral', ENDED: 'neutral' };

function OrderForm({ close, changed, domainId, available }: DialogBodyProps & { domainId: string; available: AvailableAddon[] }) {
  const { t } = useTranslation('services');
  const e = useEnum();
  const name = useLocalName();
  const orderable = available.filter((a) => a.orderable);
  const [code, setCode] = useState(orderable[0]?.code ?? '');
  const [years, setYears] = useState('');
  const [reason, setReason] = useState('');
  const w = useWrite((_: null, opts) => api.orderDomainAddon(domainId, { code, years: years ? Number(years) : undefined, reason: reason.trim() }, opts));
  const result = w.state.phase === 'done' ? w.state.result : undefined;
  return (
    <form
      className="grid gap-4"
      onSubmit={(ev) => {
        ev.preventDefault();
        void w.run(null).then((r) => r && changed());
      }}
    >
      {result ? (
        <Callout tone={result.pending_change_id ? 'warn' : 'ok'}>{result.pending_change_id ? t('domain.awaiting') : t('domain.ordered', { state: e('addonState', result.status) })}</Callout>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t('domain.orderHint')}</p>
          <Field label={t('domain.service')}>
            <select className={selectClass} value={code} onChange={(ev) => setCode(ev.target.value)}>
              {available.map((a) => (
                <option key={a.code} value={a.code} disabled={!a.orderable}>
                  {name(a)} · {e('billing', a.billing_model)}
                  {a.free ? ` · ${t('catalogue.free')}` : !a.orderable ? ` · ${t('catalogue.noPrice')}` : ''}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('domain.yearsField')}>
            <Input type="number" min={1} max={10} value={years} onChange={(ev) => setYears(ev.target.value)} />
          </Field>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!code || !reason.trim()} submit={result ? undefined : t('domain.order')} onClose={close} />
    </form>
  );
}

/** What one renewal year of this TLD costs in the default price book, line by line (reference 22 "Chi phí tên miền hiện tại"). */
function RenewCost({ tldId }: { tldId: string }) {
  const { t: tu, i18n } = useTranslation('ux');
  const e = useEnum();
  const result = useApi((signal) => api.getTldPriceBreakdown(tldId, { years: 1 }, { signal }), [tldId]);
  const op = result.status === 'success' ? result.data.operations.find((o) => o.operation === 'RENEW') : undefined;
  return (
    <Section title={tu('addons.cost')} description={tu('addons.costHint')}>
      {result.status === 'loading' ? <Loading rows={2} /> : null}
      {result.status === 'error' ? <DataError error={result.error} title={tu('addons.costError')} onRetry={result.reload} /> : null}
      {result.status === 'success' && !op ? <p className="text-sm text-muted-foreground">{tu('addons.noCost')}</p> : null}
      {op?.error ? <Callout tone="warn">{op.error.replace(/^no price available: /, '')}</Callout> : null}
      {op && !op.error ? (
        <FactRows
          rows={[
            ...op.lines.map((l) => [l.kind === 'SERVICE' ? e('lineKind', 'SERVICE') : i18n.language === 'vi' ? l.description_vi : l.description_en, <Amount key={l.code} amount={l.total} currency={op.currency} />] as const),
            [tu('addons.total'), <b key="total"><Amount amount={op.total} currency={op.currency} /></b>] as const,
          ]}
        />
      ) : null}
    </Section>
  );
}

/** Domain page tab: "Dịch vụ đang dùng" — add-ons, protection, pending changes and parking. */
export function DomainServicesTab({ detail }: { detail: DomainDetail }) {
  const { t } = useTranslation('services');
  const { t: tu } = useTranslation('ux');
  const e = useEnum();
  const name = useLocalName();
  const id = detail.domain.id;
  const result = useApi((signal) => api.getDomainServices(id, { signal }), [id]);
  if (result.status === 'loading') return <Loading rows={3} />;
  if (result.status === 'error') return <DataError error={result.error} title={t('common.loadFailed')} onRetry={result.reload} />;
  const s = result.data;
  const cols: Column<DomainAddon>[] = [
    {
      key: 'service',
      header: t('domain.col.service'),
      cell: (a) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          <span className="font-medium">{name(a)}</span>
          <span className="font-mono text-xs text-muted-foreground">{a.addon_code}</span>
          {a.mandatory ? <Badge tone="info">{t('domain.mandatory')}</Badge> : null}
        </span>
      ),
    },
    {
      key: 'state',
      header: t('domain.col.state'),
      cell: (a) => (
        <span className="inline-flex flex-wrap items-center gap-2">
          <Badge tone={STATE_TONE[a.state] ?? 'neutral'}>{e('addonState', a.state)}</Badge>
          {a.panel_pending ? (
            <Link to="/name-changes" className="text-xs text-primary hover:underline">
              {t(`domain.panelPending.${a.panel_pending}`)}
            </Link>
          ) : null}
        </span>
      ),
    },
    { key: 'billing', header: t('domain.col.billing'), cell: (a) => e('billing', a.billing_model) },
    { key: 'period', header: t('domain.col.period'), className: 'whitespace-nowrap text-xs', cell: (a) => (a.period_start ? `${formatDate(a.period_start)} → ${a.period_end ? formatDate(a.period_end) : '…'}` : t('common.none')) },
    {
      key: 'actions',
      header: t('domain.col.actions'),
      cell: (a) => (
        <span className="inline-flex flex-wrap gap-2">
          {a.state === 'ORDERED' && !a.panel_pending ? (
            <ReasonAction
              label={t('domain.apply')}
              title={`${t('domain.applyTitle')} · ${name(a)}`}
              run={(reason, opts) => api.applyDomainAddon(a.id, { reason }, opts)}
              done={(r) => e('addonState', r.state)}
              onChanged={result.reload}
            />
          ) : null}
          {!a.mandatory && (a.state === 'ORDERED' || a.state === 'ACTIVE') && a.addon_code !== 'PROTECTED' && a.panel_pending !== 'REGISTRY_UNLOCK' ? (
            <ReasonAction
              label={t('domain.cancel')}
              danger
              variant="ghost"
              title={`${t('domain.cancelTitle')} · ${name(a)}`}
              warning={t('domain.cancelWarning')}
              run={(reason, opts) => api.cancelDomainAddon(a.id, { reason }, opts)}
              done={(r) => ('pending_change_id' in r ? t('domain.awaiting') : e('addonState', r.state))}
              onChanged={result.reload}
            />
          ) : null}
        </span>
      ),
    },
  ];
  const main = (
    <div className="flex flex-col gap-5">
      <Section
        title={t('domain.protection')}
        actions={
          s.protected ? (
            <ReasonAction
              label={t('domain.unprotect')}
              icon={ShieldOff}
              danger
              title={t('domain.unprotectTitle')}
              run={(reason, opts) => api.setDomainProtection(id, { protected: false, reason }, opts)}
              done={() => t('common.done')}
              onChanged={result.reload}
            />
          ) : (
            <ReasonAction
              label={t('domain.protect')}
              icon={ShieldCheck}
              title={t('domain.protectTitle')}
              run={(reason, opts) => api.setDomainProtection(id, { protected: true, reason }, opts)}
              done={() => t('common.done')}
              onChanged={result.reload}
            />
          )
        }
      >
        <Callout tone={s.protected ? 'warn' : 'neutral'}>{s.protected ? t('domain.protectedOn') : t('domain.protectedOff')}</Callout>
      </Section>
      {s.pending_changes.length > 0 ? (
        <Section title={t('domain.pending')} flush>
          <PendingChangesTable rows={s.pending_changes} onChanged={result.reload} showDomain={false} />
        </Section>
      ) : null}
      <Section
        title={t('domain.title')}
        description={t('domain.subtitle')}
        flush
        actions={
          <ActionDialog label={t('domain.order')} icon={CirclePlus} title={t('domain.orderTitle')} onChanged={result.reload} disabled={s.available.length === 0}>
            {(p) => <OrderForm {...p} domainId={id} available={s.available} />}
          </ActionDialog>
        }
      >
        <DataTable columns={cols} rows={s.items} rowKey={(a) => a.id} empty={<EmptyState title={t('domain.empty')} />} />
      </Section>
      <Section title={tu('addons.lifecycle')} description={tu('addons.lifecycleHint')}>
        <Stepper
          label={tu('addons.lifecycle')}
          steps={[
            { id: 'order', label: tu('addons.step.order'), hint: tu('addons.step.orderHint'), count: s.items.length, state: 'done' },
            { id: 'wait', label: tu('addons.step.wait'), hint: tu('addons.step.waitHint'), count: s.items.filter((a) => a.state === 'ORDERED').length, state: s.items.some((a) => a.state === 'ORDERED') ? 'current' : 'todo' },
            { id: 'active', label: tu('addons.step.active'), hint: tu('addons.step.activeHint'), count: s.items.filter((a) => a.state === 'ACTIVE').length, state: s.items.some((a) => a.state === 'ACTIVE') ? 'done' : 'todo' },
            { id: 'end', label: tu('addons.step.end'), hint: tu('addons.step.endHint'), count: s.items.filter((a) => a.state === 'ENDED' || a.state === 'CANCELLED').length, state: 'todo' },
          ]}
        />
      </Section>
      {s.parking ? (
        <Section title={t('domain.parking')}>
          <p className="text-sm">
            {t('domain.parkingInfo', {
              state: e('parkingState', s.parking.state),
              servers: s.parking.parking_nameservers.join(', '),
              saved: s.parking.saved_nameservers.length,
              ds: s.parking.saved_ds_count,
            })}
          </p>
        </Section>
      ) : null}
    </div>
  );
  const history = s.items
    .flatMap((a) => [
      { id: `${a.id}-c`, at: a.created_at, title: tu('addons.event.ordered', { name: name(a) }), tone: 'pending' as const },
      ...(a.applied_at ? [{ id: `${a.id}-a`, at: a.applied_at, title: tu('addons.event.applied', { name: name(a) }), tone: 'ok' as const }] : []),
      ...(a.ended_at ? [{ id: `${a.id}-e`, at: a.ended_at, title: tu('addons.event.ended', { name: name(a) }), tone: 'neutral' as const, body: a.end_reason ? <SystemReason reason={a.end_reason} /> : undefined }] : []),
    ])
    .sort((x, y) => (x.at < y.at ? 1 : -1));
  return (
    <TwoColumn
      main={main}
      side={
        <>
          <RenewCost tldId={detail.domain.tld_id} />
          <Section title={tu('addons.history')}>
            <Timeline empty={tu('addons.noHistory')} items={history.map((h) => ({ ...h, time: formatDate(h.at) }))} />
          </Section>
          <Callout tone="info" title={tu('addons.onNotDoneTitle')}>
            {tu('addons.onNotDone')}
          </Callout>
        </>
      }
    />
  );
}
